import { useEffect, useState, type FormEvent } from 'react';
import { Link } from 'react-router-dom';
import { ApiError, api } from '../api/client';
import { useAuth } from '../auth';
import VoteControl from '../components/VoteControl';
import type { Tag } from '../types';

export default function TagsPage() {
  const { user } = useAuth();
  const isAdmin = user?.role === 'admin';
  const [approved, setApproved] = useState<Tag[] | null>(null);
  const [pending, setPending] = useState<Tag[] | null>(null);
  const [draft, setDraft] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function loadApproved() {
    const { data } = await api<{ data: Tag[] }>('/tags');
    setApproved(data);
  }

  async function loadPending() {
    const { data } = await api<{ data: Tag[] }>('/tags', { query: { status: 'pending' } });
    setPending(data);
  }

  async function loadAll() {
    await Promise.all([loadApproved(), loadPending()]);
  }

  useEffect(() => { loadAll(); }, []);

  async function onPropose(e: FormEvent) {
    e.preventDefault();
    setBusy(true);
    setError(null);
    try {
      await api('/tags', { method: 'POST', body: { name: draft.trim() } });
      setDraft('');
      await loadAll();
    } catch (err) {
      if (err instanceof ApiError) {
        const payload = err.payload as { errors?: Record<string, string[]>; message?: string } | null;
        const first = payload?.errors && Object.values(payload.errors)[0]?.[0];
        setError(first ?? payload?.message ?? `Submit failed (${err.status}).`);
      } else {
        setError('Submit failed.');
      }
    } finally {
      setBusy(false);
    }
  }

  async function onDelete(name: string) {
    if (!window.confirm(`Delete tag "${name}"? Removes it from every archive it's on.`)) return;
    setBusy(true);
    setError(null);
    try {
      await api(`/tags/${encodeURIComponent(name)}`, { method: 'DELETE' });
      await loadAll();
    } catch {
      setError('Delete failed.');
    } finally {
      setBusy(false);
    }
  }

  async function voteOnTag(name: string, value: -1 | 0 | 1) {
    await api(`/tags/${encodeURIComponent(name)}/vote`, { method: 'POST', body: { value } });
    await loadPending();
    if (value !== 0) await loadApproved();
  }

  async function adminApprove(name: string) {
    await api(`/tags/${encodeURIComponent(name)}/approve`, { method: 'POST' });
    await loadAll();
  }

  async function adminReject(name: string) {
    await api(`/tags/${encodeURIComponent(name)}/reject`, { method: 'POST' });
    await loadAll();
  }

  return (
    <>
      <div className="card">
        <h2 style={{ marginTop: 0 }}>{isAdmin ? 'Create a tag' : 'Propose a tag'}</h2>
        <p className="muted" style={{ fontSize: 13, marginTop: -4 }}>
          Lowercase + trimmed automatically; 30 chars max.
          {isAdmin
            ? ' Admins create approved tags directly.'
            : ' Proposals go to the vote queue below; +3 net votes approves, −3 rejects.'}
        </p>
        <form onSubmit={onPropose} style={{ display: 'flex', gap: 8 }}>
          <input
            type="text"
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            placeholder="e.g. vintage computing"
            maxLength={30}
            style={{ flex: '0 1 320px' }}
            required
          />
          <button type="submit" disabled={busy || !draft.trim()}>
            {isAdmin ? 'Create' : 'Propose'}
          </button>
        </form>
        {error && <div className="error" style={{ marginTop: 8 }}>{error}</div>}
      </div>

      {pending && pending.length > 0 && (
        <div className="card">
          <h2 style={{ marginTop: 0 }}>Awaiting community review ({pending.length})</h2>
          <p className="muted" style={{ fontSize: 13, marginTop: -4 }}>
            Vote ▲ to approve, ▼ to reject. Reaches +3 → tag becomes
            usable everywhere; reaches −3 → permanently rejected.
            {isAdmin && ' Admins can also force a decision below.'}
          </p>
          <ul style={{ listStyle: 'none', padding: 0, margin: 0, display: 'flex', flexDirection: 'column', gap: 8 }}>
            {pending.map((t) => (
              <li key={t.id} style={{
                display: 'flex', alignItems: 'center', gap: 10,
                padding: '6px 12px', borderRadius: 4,
                background: 'var(--bg-muted)', border: '1px solid var(--border-soft)',
                fontSize: 13,
              }}>
                <VoteControl
                  score={t.score ?? 0}
                  userVote={t.user_vote ?? null}
                  onVote={(v) => voteOnTag(t.name, v)}
                  compact={false}
                />
                <strong style={{ flex: 1 }}>{t.name}</strong>
                {isAdmin && (
                  <span style={{ display: 'inline-flex', gap: 6 }}>
                    <button type="button" onClick={() => adminApprove(t.name)} className="secondary" style={{ padding: '2px 8px', fontSize: 12 }}>Approve</button>
                    <button type="button" onClick={() => adminReject(t.name)} className="secondary" style={{ padding: '2px 8px', fontSize: 12 }}>Reject</button>
                  </span>
                )}
              </li>
            ))}
          </ul>
        </div>
      )}

      <div className="card">
        <h2 style={{ marginTop: 0 }}>Tags</h2>
        {approved === null ? (
          <p className="muted">Loading…</p>
        ) : approved.length === 0 ? (
          <p className="muted">No approved tags yet.</p>
        ) : (
          <ul style={{ listStyle: 'none', padding: 0, margin: 0, display: 'flex', flexWrap: 'wrap', gap: 8 }}>
            {approved.map((t) => (
              <li key={t.id} style={{
                display: 'inline-flex', alignItems: 'center', gap: 6,
                padding: '4px 10px', borderRadius: 999,
                background: 'var(--pill-bg-tag)', border: '1px solid var(--pill-border-tag)',
                fontSize: 13,
              }}>
                <Link to={`/tags/${encodeURIComponent(t.name)}`} style={{ color: 'var(--pill-fg-tag)', textDecoration: 'none' }}>
                  {t.name}
                </Link>
                <span className="muted" style={{ fontSize: 11 }}>×{t.archive_count ?? 0}</span>
                {isAdmin && (
                  <button
                    type="button"
                    onClick={() => onDelete(t.name)}
                    title="Delete tag"
                    className="link-button"
                    style={{ background: 'none', border: 0, color: 'var(--fg-error)', cursor: 'pointer', fontSize: 14, lineHeight: 1, padding: 0 }}
                    disabled={busy}
                  >×</button>
                )}
              </li>
            ))}
          </ul>
        )}
      </div>
    </>
  );
}
